import React, {
	forwardRef, useCallback, useEffect, useRef, useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import css from './CreateFirstTagDropdown.module.scss';
import CreateTagIcon from '../../../../../../image/create-tag.svg';
import { popUpTransitionClasses } from '../../../../../../styles/transitionStyles/transitionStyles';
import { notificationError } from '../../../../../../components/Notifications/callNotifcation';
import { handleKeyDown } from '../../../../../../hooks/handleKeyDown';
import { useIsMounted } from '../../../../../../hooks/lifecycle/useIsMounted';

interface CreateFirstTagDropdownProps {
	groupId: string | undefined,
	onClose?: () => void,
	bindCreatedTag: (name: string) => Promise<void>,
	isCreateModalOpen?: boolean,
	modalRef?: React.RefObject<HTMLDivElement>,
	isNeedUpdatePos?: boolean,
}

/** Компонент всплывающего окна для создаиния тега.
 * Позиционируется рядом с кнопкой "создать тег", не выходит за пределы модального окна. При необходимости,
 * корректирует позицию (например, если оно не помещается, то смещается влево).
 */
export const CreateFirstTagPopup = forwardRef<HTMLDivElement, CreateFirstTagDropdownProps>(({
	groupId,
	onClose,
	bindCreatedTag,
	isCreateModalOpen,
	modalRef,
	isNeedUpdatePos = false,
}, ref) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);
	const [tagName, setTagName] = useState('');
	const isMounted = useIsMounted();
	const [popupPosition, setPopupPosition] = useState({ top: '87%', left: '0%' });

	const onTagNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setTagName(event.target.value);
	};

	// Функция для расчета позиции всплывающего окна
	const calculatePosition = useCallback(() => {
		if (!modalRef || modalRef.current === null || !popupRef.current) return;
		let { left } = popupPosition;
		let { top } = popupPosition;

		const parentRect = modalRef.current.getBoundingClientRect();
		const popupRect = popupRef.current.getBoundingClientRect();

		if (popupRect.right > parentRect.right) {
			left = `-${popupRect.right - parentRect.right}px`;
		}

		if (popupRect.bottom + popupRect.height > parentRect.bottom) {
			top = `${parentRect.bottom - popupRect.bottom}px`;
		}

		setPopupPosition({ top, left });
	}, [modalRef]);

	useEffect(() => {
		if (!isNeedUpdatePos) {
			return;
		}
		calculatePosition();
		// Добавляем слушатель на изменение размера окна
		window.addEventListener('resize', calculatePosition);
		return () => {
			window.removeEventListener('resize', calculatePosition);
		};
	}, [calculatePosition, isNeedUpdatePos]);

	const handleCreateTag = async () => {
		if (tagName.length < 2) {
			notificationError('Тег должен содержать не менее 2-х символов');
			return;
		}

		if (isMounted.current) {
			await bindCreatedTag(tagName);
			onClose && onClose();
			setTagName('');
		}
	};

	const getKeyDownHandler = () => (e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown({
		keyCode: 'Enter',
		isActive: tagName.length >= 2,
		action: handleCreateTag,
	})(e);

	const onFocus = () => {
		if (popupRef.current) {
			popupRef.current.style.zIndex = '1001'; // Присваиваем значение z-index
		}
	};
	const onBlur = () => {
		if (popupRef.current) {
			popupRef.current.style.zIndex = '1000'; // Присваиваем значение z-index
		}
	};

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [isCreateModalOpen]);

	const style: React.CSSProperties = {
		top: isNeedUpdatePos ? popupPosition.top : undefined,
		left: isNeedUpdatePos ? popupPosition.left : undefined,
	};

	return (
		<CSSTransition
			timeout={10}
			unmountOnExit
			nodeRef={containerRef}
			in={!!groupId}
			classNames={popUpTransitionClasses}
		>
			<div
				className={css.popup}
				ref={popupRef}
				style={style}
			>
				<div className={css.create_tag_icon}>
					<CreateTagIcon />
				</div>
				<input
					placeholder="Создать тег"
					className={css.input}
					value={tagName}
					onChange={onTagNameChange}
					onKeyDown={getKeyDownHandler()}
					ref={inputRef}
					onFocus={onFocus}
					onBlur={onBlur}
				/>
				{
					tagName.length >= 2 && (
						<button
							type="button"
							className={css.button}
							onClick={handleCreateTag}
						>
							Сохранить
						</button>
					)
				}
			</div>
		</CSSTransition>
	);
});
